import type { Exchange, Operation } from "@urql/core"
import { pipe, tap } from "wonka"

type TabVisibilityExchangeOptions = {
  inactivityThreshold?: number
}

/** Exchange factory that reexecutes operations after a user returns to a tab after being inactive.
 *
 * @param options - Configuration options for the exchange.
 * @returns a new tab visibility {@link Exchange}.
 *
 * @remarks
 * The `tabVisibilityExchange` will reexecute `Operation`s with the `cache-and-network`
 * policy when a user returns to a tab after being away for the specified inactivity threshold
 * (default: 1 minute).
 *
 * This is useful for automatically refreshing data when a user returns to your application
 * after being inactive, ensuring they see up-to-date information.
 *
 * Only currently active operations will be refetched.
 */
export const tabVisibilityExchange = (
  options: TabVisibilityExchangeOptions = {},
): Exchange => {
  const inactivityThreshold = options.inactivityThreshold ?? 60_000

  return ({ client, forward }) =>
    ops$ => {
      if (typeof window === "undefined") {
        return forward(ops$)
      }

      const watchedOperations = new Map<number, Operation>()
      const observedOperations = new Map<number, number>()
      let tabHiddenTimestamp: number | null = null

      // Handle visibility change events
      const handleVisibilityChange = () => {
        if (document.visibilityState === "hidden") {
          // Tab is hidden, record the timestamp
          tabHiddenTimestamp = Date.now()
          return
        }

        // If tab is visible and we have a timestamp from when it was hidden
        if (tabHiddenTimestamp === null) {
          return
        }

        // Tab is visible again, check how long it was hidden
        const hiddenDuration = Date.now() - tabHiddenTimestamp

        if (hiddenDuration >= inactivityThreshold) {
          // If the tab was hidden for longer than the threshold, refetch operations
          watchedOperations.forEach(op => {
            client.reexecuteOperation(
              client.createRequestOperation(op.kind, op, {
                ...op.context,
                requestPolicy: "cache-and-network",
              }),
            )
          })
        }

        // Reset the timestamp
        tabHiddenTimestamp = null
      }

      // Listen for visibility changes
      window.addEventListener("visibilitychange", handleVisibilityChange)

      const processIncomingOperation = (op: Operation) => {
        if (
          (op.kind === "query" || op.kind === "subscription") &&
          !observedOperations.has(op.key)
        ) {
          observedOperations.set(op.key, 1)
          watchedOperations.set(op.key, op)
        }

        if (op.kind === "teardown" && observedOperations.has(op.key)) {
          observedOperations.delete(op.key)
          watchedOperations.delete(op.key)
        }
      }

      return forward(pipe(ops$, tap(processIncomingOperation)))
    }
}
